<template>
  <div class="page-wrapper">
    <the-email-header />

    <div class="center-content">
      <div class="quote-confirm-card">
        <v-loader v-if="loader" />

        <template v-else-if="error">
          <div class="alert alert-error">
            <p>{{ error }}</p>
          </div>
        </template>

        <template v-else-if="quoteData">
          <div class="quote-header">
            <h2>Quote Confirmation</h2>
            <p class="quote-ref">Reference: {{ quoteData.case_number }}</p>
          </div>

          <div class="quote-summary">
            <div class="summary-row">
              <span class="label">Tour</span>
              <span class="value">{{ quoteData.case_name }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Client</span>
              <span class="value">{{ quoteData.client_name }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Destination</span>
              <span class="value">{{ quoteData.destination }}</span>
            </div>
            <div class="summary-row" v-if="quoteData.start_date">
              <span class="label">Start Date</span>
              <span class="value">{{ formatDate(quoteData.start_date) }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Trip Manager</span>
              <span class="value">{{ quoteData.trip_manager }}</span>
            </div>
          </div>

          <table class="pax-table" v-if="quoteData.pax_quotes?.length">
            <thead>
              <tr>
                <th>Traveller</th>
                <th>Quote (Batch)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pq, i) in quoteData.pax_quotes" :key="i">
                <td>{{ pq.pax_name }}</td>
                <td>€ {{ formatPrice(pq.waw_price) }} <span class="batch-badge">#{{ pq.batch_id }}</span></td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td class="total-amount">€ {{ formatPrice(quoteData.total_quote) }}</td>
              </tr>
            </tfoot>
          </table>

          <div class="confirm-actions" v-if="!confirmed">
            <button class="btn-accept" :disabled="accepting" @click="acceptQuote">
              <v-loader v-if="accepting" />
              <span v-else>Accept Quote</span>
            </button>
            <p class="accept-notice">
              By clicking "Accept Quote", you confirm that you agree to the quoted price
              and your booking will be processed.
            </p>
          </div>

          <div class="alert alert-success" v-if="confirmed">
            <p>{{ successMessage }}</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup name="TheQuoteConfirm">
import { onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import api from "@/api";
import { formatPrice } from "@/composables/pricing";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import TheEmailHeader from "@/components/email/TheEmailHeader.vue";

const route = useRoute();
const { locale } = useI18n();
let savedLocale = null;
const loader = ref(false);
const accepting = ref(false);
const error = ref("");
const confirmed = ref(false);
const successMessage = ref("");
const quoteData = ref(null);

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB");
};

const fetchQuote = async () => {
  const { caseid, token } = route.query;
  if (!caseid || !token) {
    error.value = "Invalid quote link. Please check your email for the correct link.";
    return;
  }

  loader.value = true;
  try {
    const res = await api.case.getQuoteSummary(caseid, token);
    quoteData.value = res.content;
  } catch (err) {
    error.value = err?.response?.data?.message || "Failed to load quote details.";
  } finally {
    loader.value = false;
  }
};

const acceptQuote = async () => {
  const { caseid, token } = route.query;
  accepting.value = true;
  try {
    const res = await api.case.acceptQuote(caseid, token);
    confirmed.value = true;
    successMessage.value = res.message || "Quote accepted successfully! Your case has been confirmed.";
  } catch (err) {
    error.value = err?.response?.data?.message || "Failed to accept quote. Please try again or contact us.";
  } finally {
    accepting.value = false;
  }
};

onMounted(() => {
  const { lang } = route.query;
  if (lang) {
    savedLocale = locale.value;
    locale.value = lang;
  }
  fetchQuote();
});

onUnmounted(() => {
  if (savedLocale) {
    locale.value = savedLocale;
  }
});
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.center-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.quote-confirm-card {
  max-width: 600px;
  width: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 32px;
}

.quote-header {
  text-align: center;
  margin-bottom: 24px;
}

.quote-header h2 {
  font-size: 24px;
  margin: 0 0 4px 0;
  color: #2c2b2b;
}

.quote-ref {
  color: #6b6b6b;
  font-size: 14px;
  margin: 0;
}

.quote-summary {
  margin-bottom: 24px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.summary-row .label {
  font-weight: 600;
  color: #6b6b6b;
}

.summary-row .value {
  color: #2c2b2b;
}

.pax-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
}

.pax-table th,
.pax-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.pax-table th {
  background: #f8f8f8;
  font-size: 13px;
  color: #6b6b6b;
  text-transform: uppercase;
}

.batch-badge {
  display: inline-block;
  background: #e8e8e8;
  color: #6b6b6b;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 3px;
  margin-left: 4px;
}

.total-amount {
  font-weight: 700;
  font-size: 16px;
  color: #2c2b2b;
}

.confirm-actions {
  text-align: center;
}

.btn-accept {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #2c2b2b;
  color: #fff;
  border: none;
  padding: 14px 40px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-accept:hover:not(:disabled) {
  background: #1a1a1a;
}

.btn-accept:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.accept-notice {
  font-size: 13px;
  color: #6b6b6b;
  margin-top: 12px;
}

.alert {
  padding: 16px;
  border-radius: 6px;
  text-align: center;
}

.alert-error {
  background: #fee;
  color: #c33;
}

.alert-success {
  background: #efe;
  color: #363;
}
</style>
